/**
 * Team Management data layer. Every write goes through a callable so the server enforces
 * the caller's admin role, the plan's seat limit and role validation.
 * Reads stay direct (Firestore rules already scope them to the team).
 */
import { httpsCallable } from "firebase/functions";
import { collection, getDocs } from "firebase/firestore";
import { functions } from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";
import * as m from "@app/paraglide/messages.js";

/** Team roles, most to least privileged. The role is the only admin signal. */
export const TEAM_ROLES = ["admin", "manager", "editor", "viewer"];
export const DEFAULT_ROLE = "editor";

export function normalizeRole(role) {
    const value = String(role || "").trim().toLowerCase();
    return TEAM_ROLES.includes(value) ? value : null;
}

export function roleLabel(role) {
    switch (normalizeRole(role)) {
        case "admin":
            return m.team_manager_role_admin();
        case "manager":
            return m.team_manager_role_manager();
        case "editor":
            return m.team_manager_role_editor();
        case "viewer":
            return m.team_manager_role_viewer();
        default:
            return role || "";
    }
}

export function roleDescription(role) {
    switch (normalizeRole(role)) {
        case "admin":
            return m.team_manager_role_admin_desc();
        case "manager":
            return m.team_manager_role_manager_desc();
        case "viewer":
            return m.team_manager_role_viewer_desc();
        default:
            return m.team_manager_role_editor_desc();
    }
}

export function displayName(user) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return name || user.email || "";
}

export function initials(user) {
    const source = [user.firstName, user.lastName].filter(Boolean);
    if (source.length) {
        return source.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
    }
    return (user.email || "?").slice(0, 1).toUpperCase();
}

export function compareUsers(a, b) {
    return displayName(a).localeCompare(displayName(b), undefined, { sensitivity: "base" });
}

/** Transition duration, or 0 when the OS asks for reduced motion. */
const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
export function motion(ms) {
    return reduceMotion ? 0 : ms;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(email) {
    return EMAIL_PATTERN.test(String(email || "").trim());
}

/** Callable errors carry the server's message; strip Firebase's "code:" noise. */
export function errorMessage(err) {
    return String(err?.message || err || "").replace(/^FirebaseError:\s*/, "");
}

export async function loadTeam(teamId) {
    const [usersSnap, groupsSnap] = await Promise.all([
        getDocs(collection(db, "teams", teamId, "users")),
        getDocs(collection(db, "teams", teamId, "groups")),
    ]);
    const users = usersSnap.docs.map((d) => d.data()).sort(compareUsers);
    const groups = groupsSnap.docs
        .map((d) => d.data())
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    return { users, groups };
}

/** Adds or updates one member. The server rejects new members past the plan's seat limit. */
export function saveMember(teamId, user) {
    const role = normalizeRole(user.role) || DEFAULT_ROLE;
    return httpsCallable(functions, "v8AddOrUpdateUser")({
        tId: teamId,
        uId: user.id,
        email: String(user.email || "").trim().toLowerCase(),
        firstName: (user.firstName || "").trim(),
        lastName: (user.lastName || "").trim(),
        disable: !!user.disable,
        role,
        groups: user.groups || [],
        // TODO(after pricing v2 launch): drop. Only functions deployed before the role change read it.
        admin: role === "admin",
    });
}

/** Pro v2: set the number of extra users (beyond the included 3). Billing owner only; charged on the card on file. */
export function updateTeamSeats(extraSeats) {
    return httpsCallable(functions, "v8UpdateTeamSeats")({ extraSeats });
}

export function removeMember(teamId, user) {
    return httpsCallable(functions, "v8RemoveUser")({ tId: teamId, uId: user.id, email: user.email });
}

export function saveGroup(teamId, group, memberIds) {
    return httpsCallable(functions, "v8AddOrUpdateGroup")({
        tId: teamId,
        gId: group.id,
        name: group.name.trim(),
        description: (group.description || "").trim(),
        members: memberIds,
    });
}

export function removeGroup(teamId, group) {
    return httpsCallable(functions, "v8RemoveGroup")({ tId: teamId, gId: group.id });
}

export function addMemberToGroup(teamId, userId, groupId) {
    return httpsCallable(functions, "v8AddUserToGroup")({ tId: teamId, uId: userId, gId: groupId });
}

export function removeMemberFromGroup(teamId, userId, groupId) {
    return httpsCallable(functions, "v8RemoveUserFromGroup")({ tId: teamId, uId: userId, gId: groupId });
}
